export const formatSchedule = (frequency, frequencyConfig) => {
  try {
    const config = typeof frequencyConfig === 'string' 
      ? JSON.parse(frequencyConfig) 
      : (frequencyConfig || {});
    
    switch (frequency) {
      case 'daily':
        return `Daily - ${config?.time || '9:00 AM'}`;
      case 'weekly':
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = days[config?.day_of_week] || 'Monday';
        return `Weekly - ${dayName}s ${config?.time || '9:00 AM'}`;
      case 'monthly':
        return `Monthly - ${config?.day_of_month || 1}${getOrdinalSuffix(config?.day_of_month || 1)} of month`;
      case 'quarterly':
        return `Quarterly - ${config?.day_of_month || 1}${getOrdinalSuffix(config?.day_of_month || 1)} of quarter`;
      default:
        return frequency;
    }
  } catch (err) {
    console.error('Error parsing frequency config:', err);
    return frequency;
  }
};

export const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

export const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// Helper function to safely parse JSON strings
export const safeJsonParse = (value, fallback) => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (err) {
      console.warn('Failed to parse JSON:', value, err);
      return fallback;
    }
  }
  return value || fallback;
};

// Helper function to safely get recipients array
export const getRecipientsArray = (recipients) => {
  console.log('Processing recipients:', recipients);
  
  // If it's already an array, return it
  if (Array.isArray(recipients)) {
    console.log('Recipients is array:', recipients);
    return recipients.length > 0 ? recipients : [''];
  }
  
  // If it's a string, try to parse it
  if (typeof recipients === 'string') {
    console.log('Recipients is string, trying to parse:', recipients);
    try {
      const parsed = JSON.parse(recipients);
      console.log('Parsed recipients:', parsed);
      if (Array.isArray(parsed)) {
        return parsed.length > 0 ? parsed : [''];
      }
    } catch (err) {
      console.warn('Failed to parse recipients JSON:', recipients, err);
      // If it's a string but not JSON, maybe it's a single email
      if (recipients.includes('@')) {
        console.log('Treating as single email:', recipients);
        return [recipients];
      }
    }
  }
  
  // Fallback to single empty string
  console.log('Using fallback for recipients');
  return [''];
};

// Helper functions for date ranges
export const getDateRangeStart = (dateRangeConfig) => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);
  return thirtyDaysAgo.toISOString().split('T')[0];
};

export const getDateRangeEnd = (dateRangeConfig) => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Default form structure
export const getDefaultReportForm = () => ({
  name: '',
  description: '',
  report_type: 'performance',
  frequency: 'weekly',
  frequency_config: {
    day_of_week: 1,
    time: '09:00'
  },
  timezone: 'UTC',
  date_range_type: 'rolling',
  date_range_config: {
    period: 'last_30_days'
  },
  data_filters: {},
  report_sections: ['executive_summary', 'performance_metrics', 'trend_analysis'],
  format_settings: {
    format: 'pdf',
    style: 'executive'
  },
  recipients: [''],
  delivery_settings: {
    subject: 'Scheduled Report',
    body_template: 'default'
  }
});

// utils/reportingValidation.js - Validation utilities for reporting forms

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateReportForm = (formData) => {
  const errors = [];

  // Required field validation
  if (!formData.name || formData.name.trim() === '') {
    errors.push('Report name is required');
  }

  if (!formData.business_id) {
    errors.push('Business ID is required');
  }

  if (!formData.created_by) {
    errors.push('Created by field is required');
  }

  // Recipients validation
  if (!formData.recipients || !Array.isArray(formData.recipients) || formData.recipients.length === 0) {
    errors.push('At least one recipient email is required');
  } else {
    const invalidEmails = formData.recipients.filter(email => !validateEmail(email));
    if (invalidEmails.length > 0) {
      errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
    }
  }

  // Frequency validation
  if (!['daily', 'weekly', 'monthly', 'quarterly'].includes(formData.frequency)) {
    errors.push('Invalid frequency selected');
  }

  // Report type validation
  if (!['performance', 'analytics', 'custom', 'executive'].includes(formData.report_type)) {
    errors.push('Invalid report type selected');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateScheduledReportData = (reportData) => {
  const validation = validateReportForm(reportData);
  
  if (!validation.isValid) {
    const error = new Error(validation.errors[0]); // Return first error
    error.validationErrors = validation.errors;
    throw error;
  }
  
  return true;
};

export const getRequiredLocalStorageValues = () => {
  const businessId = localStorage.getItem('selectedBusinessId');
  const userId = localStorage.getItem('user');
  
  if (!businessId) {
    throw new Error('No business selected. Please select a business first.');
  }
  
  if (!userId) {
    throw new Error('User session expired. Please log in again.');
  }
  
  return { businessId, userId };
};